import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Merci ! Ta demande a été envoyée. Je reviendrai vers toi très rapidement.");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Une erreur est survenue. Réessaie ou contacte-moi directement par email.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Schema.org LocalBusiness structured data
  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Croissance Consulting",
    "image": "https://croissanceconsulting.net/logo.png",
    "email": "christophe.marrama@croissanceconsulting.net",
    "telephone": "+33674245563",
    "areaServed": [
      {
        "@type": "City",
        "name": "Annecy"
      },
      {
        "@type": "AdministrativeArea",
        "name": "Savoie"
      },
      {
        "@type": "AdministrativeArea",
        "name": "Haute-Savoie"
      }
    ],
    "priceRange": "Devis sur demande"
  };

  return (
    <>
      <Helmet>
        <title>Contact consultant boulangerie pâtisserie Annecy | Croissance Consulting</title>
        <meta name="description" content="Contactez Christophe Marrama pour échanger sur vos besoins en rentabilité, marge, organisation ou accompagnement en boulangerie/pâtisserie à Annecy, Savoie et Haute-Savoie." />
        <link rel="canonical" href="https://croissanceconsulting.net/contact" />
        <script type="application/ld+json">
          {JSON.stringify(businessSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#FBF7EF]">
        {/* Hero Section */}
        <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#4A3A2A] font-poppins mb-6">
              Proposer un échange
            </h1>
            <p className="text-lg sm:text-xl text-[#2D2D2D] mb-4">
              Remplis ce formulaire et je reviendrai vers toi rapidement pour échanger sur tes besoins.
            </p>
            
            {/* Lien interne vers les offres */}
            <div className="bg-[#6B7A3A]/5 rounded-xl p-4 inline-block">
              <p className="text-sm text-[#2D2D2D]">
                <span className="font-medium">Vous venez pour :</span>{" "}
                <Link to="/audit-rentabilite-boulangerie" className="text-[#B65C3A] hover:underline">
                  Audit rentabilité
                </Link>{" / "}
                <Link to="/offres" className="text-[#B65C3A] hover:underline">
                  Diagnostic immersion
                </Link>{" / "}
                <Link to="/stop-aux-invendus" className="text-[#B65C3A] hover:underline">
                  Stop aux invendus
                </Link>
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-8">
              Formulaire de contact
            </h2>
            <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* First Name and Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="firstName" className="text-[#2D2D2D] font-medium">
                      Prénom *
                    </Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="mt-2 border-[#4A3A2A]/20 focus:border-[#B65C3A] h-12"
                      placeholder="Ton prénom"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-[#2D2D2D] font-medium">
                      Nom *
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="mt-2 border-[#4A3A2A]/20 focus:border-[#B65C3A] h-12"
                      placeholder="Ton nom"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-[#2D2D2D] font-medium">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 border-[#4A3A2A]/20 focus:border-[#B65C3A] h-12"
                    placeholder="ton.email@exemple.com"
                  />
                </div>

                {/* Phone and Company */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-[#2D2D2D] font-medium">
                      Téléphone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2 border-[#4A3A2A]/20 focus:border-[#B65C3A] h-12"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-[#2D2D2D] font-medium">
                      Entreprise
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="mt-2 border-[#4A3A2A]/20 focus:border-[#B65C3A] h-12"
                      placeholder="Nom de ton établissement"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-[#2D2D2D] font-medium">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="mt-2 border-[#4A3A2A]/20 focus:border-[#B65C3A]"
                    placeholder="Parle-moi de ta situation et de tes besoins..."
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#B65C3A] hover:bg-[#9d4e2f] text-white font-medium py-6 text-lg"
                  >
                    {isSubmitting ? "Envoi en cours..." : "Envoyer ma demande"}
                  </Button>
                </div>
              </form>

              {/* SEO Help Text */}
              <div className="mt-6 pt-6 border-t border-[#4A3A2A]/10">
                <p className="text-sm text-[#2D2D2D] text-center">
                  <strong>Réponse sous 24–48h.</strong> Pensez à préciser votre ville, votre activité (boulangerie/pâtisserie) et votre objectif principal (marge, invendus, achats, organisation).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Info Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#4A3A2A] font-poppins text-center mb-8">
              Coordonnées directes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#FBF7EF] rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#B65C3A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-[#B65C3A]" size={24} />
                </div>
                <h3 className="font-semibold text-[#4A3A2A] mb-2">Email</h3>
                <a
                  href="mailto:christophe.marrama@croissanceconsulting.net"
                  className="text-sm text-[#2D2D2D] hover:text-[#B65C3A] break-all"
                >
                  christophe.marrama@croissanceconsulting.net
                </a>
              </div>

              <div className="bg-[#FBF7EF] rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#6B7A3A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="text-[#6B7A3A]" size={24} />
                </div>
                <h3 className="font-semibold text-[#4A3A2A] mb-2">Téléphone</h3>
                <a
                  href="tel:+33674245563"
                  className="text-sm text-[#2D2D2D] hover:text-[#6B7A3A]"
                >
                  06 74 24 55 63
                </a>
              </div>

              <div className="bg-[#FBF7EF] rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#B65C3A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-[#B65C3A]" size={24} />
                </div>
                <h3 className="font-semibold text-[#4A3A2A] mb-2">Zone d'intervention</h3>
                <p className="text-sm text-[#2D2D2D]">
                  Annecy • Savoie/Haute-Savoie
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
